import { useEffect, useState } from 'react';
import Moment from 'react-moment';
import { useHistory } from 'react-router-dom';
import Loading from '../../common/loading';
import Table from '../../common/table';
import { Urls } from '../../../config/urls';
import l from '../../../lang';
import './styles.scss';
import ChipStatus from '../../common/chip-status';
import { createDate } from '../../../utils/index';
import Dialog from '../../common/dialog';
import AddRefund from '../refunds/add-refund';
import { Routes } from '../../../config/routes';
import { PaymentMethodTypes, SHOPIFY } from '../../../constants';
import TopDashboardSection from '../../common/top-dashboard-section';
import TransactionDetail from './detail';
import ButtonCustom from '../../common/__deprecated__/button-custom';
import ButtonMore from '../../common/button-more';
import { Tooltip } from '../../common/tooltip';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-unfilled.svg';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import { Empty, EmptyNoFound } from './empty';
import TransactionFilter from './filter';
import { useMediaQuery } from '@mui/material';
import ExportTransactions from './export';
import { useTransactions } from '../../../store/transactions';
import { Filters } from '../../../store/interfaces';
import { useRefunds } from '../../../store/refunds';

const Transactions = () => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const { loading, error, allTransactions, getTransactions } = useTransactions();
  const {
    requestRefund: { success: requestRefundSucess },
  } = useRefunds();

  const history = useHistory();
  const [transactionToRefund, setTransactionToRefund] = useState<any>(null);
  const [transDetail, setTransDetail] = useState<any>(null);
  const [shopifyRefund, setShopifyRefund] = useState<any>(null);

  const initFilters = { search_by: '', status: '', country: '', from: '', to: '', paymentMethodId: '' };
  const [filter, setFilter] = useState<Filters>(initFilters);

  useEffect(() => {
    getTransactions(0, filter);
  }, [filter]);

  const hasFilter = Object.values(filter).some(value => value !== '');
  const hasTransactions = allTransactions && allTransactions?.data?.length > 0;

  const EmptyBody = () => {
    if (!error && !hasTransactions && !hasFilter) {
      return <Empty />;
    }
    if (error || !hasTransactions) {
      return <EmptyNoFound />;
    }
    return null;
  };

  return (
    <div className="transactions">
      <TopDashboardSection title={l('menu.item.TRANSACTIONS')} />
      <Card>
        <CardDescription>{l('transactions.subheadline')}</CardDescription>
        {isDesktop && <ExportTransactions />}
        <div className="transactions__content">
          <div className="transactions__filter_wrapper">
            <TransactionFilter
              loading={loading}
              appliedFilters={filter}
              clearFilters={() => setFilter(initFilters)}
              changeFilters={filters => setFilter(filters)}
            />
          </div>
          {loading && (
            <div className="transactions__loading">
              <Loading />
            </div>
          )}
          {!loading && (
            <div className="transactions__table">
              <Table
                withBtnMenu
                emptyBody={<EmptyBody />}
                headers={[
                  <span className="transactions__column__left">{l('transactions.createDate')}</span>,
                  <span className="transactions__column__left">{l('transactions.country')}</span>,
                  <span className="transactions__column__left">{l('transactions.name')}</span>,
                  <span className="transactions__column__left">{l('transactions.paymentMethod')}</span>,
                  <span className="transactions__column__right transactions__title">
                    {l('transactions.totalAmount')}&nbsp;
                    <Tooltip title={l('transactions.totalAmount.tooltip')}>
                      <InfoIcon />
                    </Tooltip>
                  </span>,
                  <span>{l('transactions.status')}</span>,
                ]}
                body={
                  (hasTransactions &&
                    !error &&
                    allTransactions?.data?.map((trans: any) => {
                      return [
                        <span className="transactions__column__left">
                          <Moment format="DD/MM/YYYY">{createDate(trans.creationDate)}</Moment>
                        </span>,
                        <span className="transactions__column__left transactions__country">
                          <img
                            alt=""
                            className="transactions__country__flag"
                            src={`${Urls.flags.replace(':code', trans.country)}`}
                          />
                          &nbsp;
                          <span>{l(`countryCode.${trans.country}`)}</span>
                        </span>,
                        <span className="transactions__column__left">
                          {trans.clientFirstName} {trans.clientLastName}
                        </span>,
                        <span className="transactions__column__left">{trans.paymentMethod}</span>,
                        <span className="transactions__column__right">
                          {trans.currency} {trans.localAmount}
                        </span>,
                        <div>
                          <ChipStatus status={trans.status} text={l(`transactions.status.${trans.status}`)} />
                        </div>,
                        <ButtonMore onClick={() => setTransDetail(trans)} />,
                      ];
                    })) ||
                  null
                }
                rowsPerPageOptions={[allTransactions?.size]}
                rowsLength={allTransactions?.totalElements}
                rowsPerPage={allTransactions?.size}
                page={allTransactions?.page}
                handleChangePage={(event, newPage) => {
                  getTransactions(newPage, filter);
                }}
                maxHeight={undefined}
              />
              {transDetail && !shopifyRefund && (
                <Dialog
                  dividers={false}
                  unpaddingFooter
                  unpaddingContent
                  fullScreenMobile
                  titleCenter
                  title={l('transactions.details')}
                  handleClose={() => setTransDetail(null)}
                  body={<TransactionDetail detail={transDetail} />}
                  footer={
                    transDetail.status === 'COMPLETED' ? (
                      <ButtonCustom
                        size="full-bottom"
                        variant="contained"
                        text={l('transactions.addRefund')}
                        onClick={() => {
                          if (transDetail.checkoutType === SHOPIFY) {
                            setShopifyRefund(true);
                          } else {
                            const transactionToRefund = transDetail;
                            setTransactionToRefund(transactionToRefund);
                            setTransDetail(null);
                          }
                        }}
                      />
                    ) : null
                  }
                />
              )}
              {shopifyRefund && (
                <Dialog
                  dividers={false}
                  titleCenter
                  title={l('transactions.details.shopifyRefund.title')}
                  handleClose={() => setShopifyRefund(false)}
                  body={<div>{l('transactions.details.shopifyRefund.body')}</div>}
                />
              )}
            </div>
          )}
        </div>
        {transactionToRefund && (
          <Dialog
            dividers={false}
            unpaddingContent
            fullScreenMobile
            type={transactionToRefund.paymentMethodType === PaymentMethodTypes.CREDIT_CARD ? 'sm' : 'md'}
            title={l('addRefund.title')}
            body={
              <AddRefund
                beneficiary={`${transactionToRefund.clientFirstName} ${transactionToRefund.clientLastName}`.trim()}
                country={transactionToRefund.country}
                transactionId={transactionToRefund.id}
                invoiceId={transactionToRefund.invoiceId}
                currency={transactionToRefund.currency}
                amount={transactionToRefund.localAmount}
                paymentMethodType={transactionToRefund.paymentMethodType}
              />
            }
            handleClose={() => {
              setTransactionToRefund(null);
              if (requestRefundSucess) {
                history.push(Routes.REFUNDS);
              }
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default Transactions;
